import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '../../config';
import Loader from '../../components/ui/Loader';
import { useAuth } from '../../components/shared/AuthContext';
import { PageLayout, Table, Button, DatePicker } from "../../components/ui";
import ReportHeader from "../../components/reports/ReportHeader";

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const getAllEmis = async (filterType, selectedDate, fromDate, toDate, userBranchId = null, isSuperAdmin = false) => {
  try {
    const params = new URLSearchParams({
      filterType,
      selectedDate: selectedDate || '',
      fromDate: fromDate || '',
      toDate: toDate || '',
      branchId: userBranchId || '',
      isSuperAdmin: isSuperAdmin
    });

    const response = await fetch(`${API_BASE_URL}/api/v2/loan-demand-report?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch loan demand report: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching loan demand report:', error);
    return [];
  }
};

const LoanDemand = () => {
  const { roleName, data } = useAuth();
  const isSuperAdmin = roleName === "superadmin";
  const userBranchId = data?.branchId;
  const shouldFilterByBranch = !isSuperAdmin && userBranchId;

  const [allEmis, setAllEmis] = useState([]);
  const [filterType, setFilterType] = useState('monthly');
  const [selectedDate, setSelectedDate] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [weekDate, setWeekDate] = useState('');

  // Compute Sunday–Saturday range for a given date string
  const getWeekRange = (dateStr) => {
    if (!dateStr) return { weekStart: '', weekEnd: '' };
    const date = new Date(dateStr);
    const day = date.getDay(); // 0 = Sunday
    const sunday = new Date(date);
    sunday.setDate(date.getDate() - day);
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);
    const fmt = (d) => d.toISOString().split('T')[0];
    return { weekStart: fmt(sunday), weekEnd: fmt(saturday) };
  };

  const { weekStart, weekEnd } = getWeekRange(weekDate);
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = allEmis.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(allEmis.length / rowsPerPage);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Fetch branches for SuperAdmin
  useEffect(() => {
    if (isSuperAdmin) {
      const fetchBranches = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/branches`);
          if (response.ok) {
            const data = await response.json();
            setBranches(data);
          }
        } catch (error) {
          console.error("Error fetching branches:", error);
        }
      };
      fetchBranches();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    const fetchAllEmis = async () => {
      setLoading(true);
      try {
        const branchIdToFilter = isSuperAdmin ? selectedBranch : userBranchId;

        // For weekly filter, derive from/to from the computed Sun–Sat range
        const effectiveFrom = filterType === 'weekly' ? weekStart : fromDate;
        const effectiveTo   = filterType === 'weekly' ? weekEnd   : toDate;

        // Don't fetch if weekly is selected but no date has been picked yet
        if (filterType === 'weekly' && !weekDate) {
          setAllEmis([]);
          setLoading(false);
          return;
        }

        const emis = await getAllEmis(
          filterType === 'weekly' ? 'dateRange' : filterType,
          selectedDate,
          effectiveFrom,
          effectiveTo,
          branchIdToFilter,
          isSuperAdmin
        );
        setAllEmis(emis);
      } catch (error) {
        console.error("Error fetching EMIs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAllEmis();
  }, [filterType, selectedDate, fromDate, toDate, weekDate, weekStart, weekEnd, shouldFilterByBranch, userBranchId, isSuperAdmin, selectedBranch]);

  const handleFilterChange = (e) => {
    const newFilterType = e.target.value;
    setFilterType(newFilterType);
    setCurrentPage(1);

    if (newFilterType !== 'daily') {
      setSelectedDate('');
    }
    if (newFilterType !== 'dateRange') {
      setFromDate('');
      setToDate('');
    }
    if (newFilterType !== 'weekly') {
      setWeekDate('');
    }
  };

  const exportToExcel = () => {
    const data = allEmis.map((emi, index) => ({
      'S.No': index + 1,
      'Customer ID': emi.customerUniqueId,
      'Customer Name': emi.customerName,
      'Due No': emi.dueNo,
      'Due Date': emi.dueDate,
      'Phone Number': emi.phoneNumber,
      'Total (₹)': parseFloat(emi.total).toFixed(2),
      'Principal (₹)': parseFloat(emi.principal).toFixed(2),
      'Interest (₹)': parseFloat(emi.interest).toFixed(2),
      'Outstanding (₹)': parseFloat(emi.outstanding).toFixed(2),
      'Status': emi.received,
      'Account Balance (₹)': emi.accountBalance.toFixed(2),
    }));

    let reportPeriod;
    if (filterType === 'dateRange') {
      reportPeriod = `${fromDate}_to_${toDate}`;
    } else if (filterType === 'daily') {
      reportPeriod = selectedDate;
    } else {
      reportPeriod = filterType;
    }

    const worksheetData = [
      [
        'S.No',
        'Customer ID',
        'Customer Name',
        'Due No',
        'Due Date',
        'Phone Number',
        'Total (₹)',
        'Principal (₹)',
        'Interest (₹)',
        'Outstanding (₹)',
        'Status',
        'Account Balance (₹)',
      ],
      ...data.map((row) => Object.values(row)),
      [],
      ['Total Amount Due:', '', '', '', '', '', allEmis.reduce((sum, emi) => sum + parseFloat(emi.total || 0), 0).toFixed(2)],
      ['Total Outstanding:', '', '', '', '', '', allEmis.reduce((sum, emi) => sum + parseFloat(emi.outstanding || 0), 0).toFixed(2)],
    ];
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let c = range.s.c; c <= range.e.c; c++) {
      const headerCell = XLSX.utils.encode_cell({ r: 0, c });
      if (!worksheet[headerCell]) continue;
      worksheet[headerCell].s = { font: { bold: true } };
    }
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Loan Demand Report');
    XLSX.writeFile(workbook, `Loan_Demand_Report_${reportPeriod}.xlsx`);
  };

  const filteredEmis = (selectedCustomer
    ? currentRows.filter((emi) => emi.customerUniqueId === selectedCustomer)
    : currentRows
  ).filter((emi) => emi.received !== 'foreclosing');

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-4">
      {/* Top Filter and Actions Bar using reusable ReportHeader */}
      <ReportHeader
        noWrap
        filters={
          <>
            {/* Branch Filter for SuperAdmin */}
            {isSuperAdmin && (
              <div className="relative w-[135px] shrink-0 mt-1">
                <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                  Branch
                </label>
                <div className="relative">
                  <select
                    value={selectedBranch}
                    onChange={(e) => setSelectedBranch(e.target.value)}
                    className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3 pr-7 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium truncate"
                  >
                    <option value="">All Branches</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5 text-ink-400">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            )}

            {/* Filter Period */}
            <div className="relative w-[115px] shrink-0 mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Period
              </label>
              <div className="relative">
                <select
                  value={filterType}
                  onChange={handleFilterChange}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3 pr-7 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="dateRange">Date Range</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Select Date (Daily) */}
            {filterType === 'daily' && (
              <div className="w-[130px] shrink-0 mt-1">
                <DatePicker
                  label="Select Date"
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date ? date.toISOString().split('T')[0] : '')}
                  placeholder="YYYY-MM-DD"
                />
              </div>
            )}

            {/* Week Picker (Weekly) */}
            {filterType === 'weekly' && (
              <div className="w-[135px] shrink-0 mt-1">
                <DatePicker
                  label="Week Date"
                  selected={weekDate}
                  onChange={(date) => { setWeekDate(date ? date.toISOString().split('T')[0] : ''); setCurrentPage(1); }}
                  placeholder="YYYY-MM-DD"
                />
              </div>
            )}

            {/* Date Range Selection */}
            {filterType === 'dateRange' && (
              <>
                <div className="w-[130px] shrink-0 mt-1">
                  <DatePicker
                    label="From Date"
                    selected={fromDate}
                    onChange={(date) => setFromDate(date ? date.toISOString().split('T')[0] : '')}
                    placeholder="YYYY-MM-DD"
                  />
                </div>
                <div className="w-[130px] shrink-0 mt-1">
                  <DatePicker
                    label="To Date"
                    selected={toDate}
                    onChange={(date) => setToDate(date ? date.toISOString().split('T')[0] : '')}
                    placeholder="YYYY-MM-DD"
                  />
                </div>
              </>
            )}

            {/* Customer Filter */}
            <div className="relative w-[155px] max-w-[175px] shrink-0 mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Customer
              </label>
              <div className="relative">
                <select
                  value={selectedCustomer || ''}
                  onChange={(e) => setSelectedCustomer(e.target.value || null)}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3 pr-7 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium truncate"
                >
                  <option value="">All Customers</option>
                  {[...new Set(allEmis.map((emi) => emi.customerUniqueId))].map((customerId) => (
                    <option key={customerId} value={customerId}>
                      {allEmis.find((emi) => emi.customerUniqueId === customerId)?.customerName || customerId}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </>
        }
        actions={
          <div className="flex flex-nowrap items-center gap-2 shrink-0">
            {/* Total Amount Due */}
            <div className="bg-white px-2.5 h-[38px] rounded-lg border border-border-light shadow-xs flex flex-col items-center justify-center text-center shrink-0 min-w-[125px] whitespace-nowrap">
              <span className="text-[9px] font-bold text-ink-400 uppercase tracking-wider leading-none mb-0.5 whitespace-nowrap">
                Total Amount Due
              </span>
              <span className="text-xs font-bold text-emerald-600 leading-tight whitespace-nowrap">
                ₹{allEmis.reduce((sum, emi) => sum + parseFloat(emi.total || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>

            {/* Total Outstanding */}
            <div className="bg-white px-2.5 h-[38px] rounded-lg border border-border-light shadow-xs flex flex-col items-center justify-center text-center shrink-0 min-w-[125px] whitespace-nowrap">
              <span className="text-[9px] font-bold text-ink-400 uppercase tracking-wider leading-none mb-0.5 whitespace-nowrap">
                Total Outstanding
              </span>
              <span className="text-xs font-bold text-rose-600 leading-tight whitespace-nowrap">
                ₹{allEmis.reduce((sum, emi) => sum + parseFloat(emi.outstanding || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>

            <Button
              onClick={exportToExcel}
              variant="primary"
              size="sm"
              className="shadow-xs h-[38px] px-3 font-bold text-xs whitespace-nowrap shrink-0"
            >
              <i className="fa-solid fa-file-excel mr-1.5"></i>
              Export to Excel
            </Button>
          </div>
        }
      />
      {/* Main Table */}
      <div className="flex-grow flex flex-col min-h-0">
        <Table>
          <Table.Head>
            <tr>
              <Table.HeadCell align="center">S.No</Table.HeadCell>
              <Table.HeadCell align="center">Customer ID</Table.HeadCell>
              <Table.HeadCell align="left">Customer Name</Table.HeadCell>
              <Table.HeadCell align="center">Due No</Table.HeadCell>
              <Table.HeadCell align="center">Due Date</Table.HeadCell>
              <Table.HeadCell align="center">Phone Number</Table.HeadCell>
              <Table.HeadCell align="right">Total</Table.HeadCell>
              <Table.HeadCell align="right">Principal</Table.HeadCell>
              <Table.HeadCell align="right">Interest</Table.HeadCell>
              <Table.HeadCell align="right">Outstanding</Table.HeadCell>
              <Table.HeadCell align="center">Status</Table.HeadCell>
              <Table.HeadCell align="right">Account Balance</Table.HeadCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <Table.Row>
                <Table.Cell colSpan={12} align="center" className="py-10">
                  <div className="flex flex-col items-center justify-center py-4">
                    <Loader size="md" />
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : filteredEmis.length > 0 ? (
              filteredEmis.map((emi, index) => (
                <Table.Row key={emi.id}>
                  <Table.Cell align="center" className="text-ink-400">{indexOfFirstRow + index + 1}</Table.Cell>
                  <Table.Cell align="center" className="font-semibold text-primary">{emi.customerUniqueId}</Table.Cell>
                  <Table.Cell align="left" className="font-semibold text-ink-900">{emi.customerName}</Table.Cell>
                  <Table.Cell align="center" className="font-semibold">{emi.dueNo}</Table.Cell>
                  <Table.Cell align="center">{emi.dueDate}</Table.Cell>
                  <Table.Cell align="center">{emi.phoneNumber}</Table.Cell>
                  <Table.Cell align="right" className="font-bold text-ink-850">
                    ₹{parseFloat(emi.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Table.Cell>
                  <Table.Cell align="right" className="text-ink-600">
                    ₹{parseFloat(emi.principal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Table.Cell>
                  <Table.Cell align="right" className="text-ink-600">
                    ₹{parseFloat(emi.interest || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Table.Cell>
                  <Table.Cell align="right" className="font-bold text-rose-600">
                    ₹{parseFloat(emi.outstanding || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Table.Cell>
                  <Table.Cell align="center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                      emi.received?.toLowerCase() === 'received'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-rose-50 text-rose-700 border-rose-200'
                    }`}>
                      {emi.received.toUpperCase()}
                    </span>
                  </Table.Cell>
                  <Table.Cell align="right" className="font-bold text-[#0369a1]">
                    ₹{parseFloat(emi.accountBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Table.Cell>
                </Table.Row>
              ))
            ) : (
              <Table.EmptyRow colSpan={12} message="No EMIs found" />
            )}
          </Table.Body>
        </Table>

        {/* Pagination */}
        {allEmis.length > 0 && (
          <div className="flex items-center justify-between mt-2 bg-white px-3 py-1.5 rounded-lg border border-border-light shadow-xs text-xs">
            <div className="flex items-center gap-2 text-xs text-ink-500">
              <span className="font-medium">Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                className="bg-transparent border border-border-medium rounded px-1.5 py-0.5 h-[26px] focus:outline-none font-semibold text-ink-700 text-xs"
              >
                {[5, 10, 20, 50, 100].map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <span className="text-xs text-ink-500 font-medium">
              Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, allEmis.length)} of {allEmis.length} entries
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="h-[26px] px-2 text-xs font-semibold"
              >
                First
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="h-[26px] px-2 text-xs font-semibold"
              >
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="h-[26px] px-2 text-xs font-semibold"
              >
                Next
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="h-[26px] px-2 text-xs font-semibold"
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default LoanDemand;
